import { pt2px } from '@/utils/converter'
import React, { useRef, useEffect, useState } from 'react'
import HTMLFlipBook from 'react-pageflip'
import Image from 'next/image'
import { useRouter } from 'next/router'

const formatImageUrl = (url: string | undefined) => {
  if (!url) return '/placeholder.png';
  
  if (url.startsWith('http')) return url;
  
  const cleanPath = url.replace(/^\/+/, '');
  const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || '';
  return `${cdnUrl}/${cleanPath}`;
};

interface Props {
  pages: any
  pages_predefined_texts: any
  pages_dedication_texts: any
  product: any
  inputDedication: any
  cover_pages_predefined_texts: any
  cover_pages: any
  bookView?: string
  isRtl?: boolean
  width?: number
  height?: number
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
  desktopRef?: any
  imageSelected?: any
  dedicationImage?: any
  isProductRtlInBook?: any
  isInIsrael?: any
}
export default function Book({
  pages,
  pages_predefined_texts,
  isRtl = true,
  pages_dedication_texts,
  product,
  inputDedication,
  cover_pages,
  cover_pages_predefined_texts,
  imageSelected,
  dedicationImage,
  width = 600,
  height = 600,
  minWidth = 415,
  maxWidth = 600,
  minHeight = 600,
  maxHeight = 833,
  desktopRef,
  bookView='double',
  isProductRtlInBook,
  isInIsrael
}: Props) {
  const modified_pages: any = []
  const [forceUpdate, setForceUpdate] = useState(0);
  const router = useRouter()
  const name =
    router.query.name != undefined ? (router.query.name as string) : ''
  const [imageWidth, setImageWidth] = React.useState(300)

  // Determine cover pages based on RTL
  const startCoverPage = isProductRtlInBook ? '1' : '0';
  const endCoverPage = isProductRtlInBook ? '0' : '1';

  useEffect(() => {
    setForceUpdate(prev => prev + 1);
    
    setTimeout(() => {
      const elem = document.querySelector('.--simple img')
      if (elem && elem.clientWidth !== 0) {
        setImageWidth(elem.clientWidth)
      }
    }, 100);
  }, [bookView]);

  const handleImageLoad = (event: any) => {
    const elem = document.querySelector('.--simple img')
    if (elem) {
      if (elem.clientWidth != 0) {
        setImageWidth(elem.clientWidth)
      }
    }
  }
  
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) {
        const elem = document.querySelector('.--simple img')
        if (elem) {
          if (elem.clientWidth != 0) {
            setImageWidth(elem.clientWidth)
          }
        }
      }
    })
  }
  
  React.useEffect(() => {
    if (imageWidth === 0) {
      const elem = document.querySelector('.--simple img')
      if (elem) {
        if (elem.clientWidth != 0) {
          setImageWidth(elem.clientWidth)
        }
      }
    }
  }, [imageWidth, bookView])

  const getTextAlignment = (text_align: string) => {
    if (text_align == 'C') {
      return 'center'
    }
    if (text_align == 'R') {
      return 'right'
    }
    if (text_align == 'L') {
      return 'left'
    }
    return 'left'
  }
  
  if (pages) {
    for (let index = pages.length - 1; index >= 0; index--) {
      if ((index + 1) % 2 === 0) {
        continue
      }
      modified_pages.push(pages[index + 1], pages[index])
    }

    let filteredPages : any;
    
    // Filter out pages based on dedication image selection
    filteredPages = modified_pages.filter((page: any, index: number) => {
      if (isProductRtlInBook) {
        const currentPageIndex = isInIsrael ? (modified_pages.length - index - 1) : (modified_pages.length - index);
        if (currentPageIndex === 1 && (imageSelected !== 'yes' || !dedicationImage)) return false;
        if (currentPageIndex === 0 && imageSelected === 'yes' && dedicationImage) return false;
      } else {
        if (index === (modified_pages.length - 2) && (imageSelected !== 'yes' || !dedicationImage)) return false;
        if (index === (modified_pages.length - 1) && imageSelected === 'yes' && dedicationImage) return false;
      }
      return true;
    });

    const dedicationPageIndex = isProductRtlInBook ? 
      (imageSelected === 'yes' && dedicationImage ? 1 : 2) : 
      (imageSelected === 'yes' && dedicationImage ? modified_pages.length - 3 : modified_pages.length - 2);
    
    const dedicationPage = modified_pages[dedicationPageIndex];
    const whitePage = isInIsrael ? modified_pages[modified_pages.length - 1] : modified_pages[0];

    const emptyWhitePage = null;

    if(isProductRtlInBook) {
      if (imageSelected === 'yes' && dedicationImage) {
        if(isInIsrael)
        {
          filteredPages = [
          filteredPages[1],
          ...filteredPages.slice(0, -2),
          filteredPages[filteredPages.length - 1],
          whitePage,
        ];
        }else{
          filteredPages = [
            whitePage,
            filteredPages[1],
            ...filteredPages.slice(3),
            whitePage,
          ];
        }
      } else {

        if(isInIsrael)
        {
          filteredPages = [
            filteredPages[0],
            ...filteredPages
          ];
        }else {
          filteredPages = [
            whitePage,
            ...filteredPages.slice(2),
            modified_pages[modified_pages.length-1],
            whitePage,
          ];
        }
      }
    } else {
      if (imageSelected === 'yes' && dedicationImage) {
        filteredPages = [
          emptyWhitePage,
          ...filteredPages.slice(0, -2),
          filteredPages[filteredPages.length - 1],
          emptyWhitePage,
        ];
        } else {
          filteredPages = [
            emptyWhitePage,
            ...filteredPages.slice(0, -1),
            filteredPages[filteredPages.length - 1],
          ];
        }
    }

    const startPage = isProductRtlInBook ? filteredPages.length + 1 : 0;

    // For RTL books
    if (isProductRtlInBook) {
      const renderPages = isInIsrael ? filteredPages : [...filteredPages].reverse();
      const totalPages = renderPages.length;
      return (
        // @ts-ignore: Unreachable code error
        <HTMLFlipBook
          key={`book-${isProductRtlInBook ? 'rtl' : 'ltr'}-${bookView}-${forceUpdate}`}
          width={width}
          height={height}
          size="stretch"
          minWidth={minWidth}
          maxWidth={maxWidth}
          minHeight={minHeight}
          startPage={startPage}
          showCover={true}
          maxHeight={maxHeight}
          ref={desktopRef}
          maxShadowOpacity={0.5}
          mobileScrollSupport={true}>
          
          {/* First Cover (page 1 for RTL) */}
          <div className="relative">
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == startCoverPage)[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == startCoverPage,
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className="texts absolute"
                    key={`${bookView}-page-${idx}-${forceUpdate}`}
                    id={item.X_coord_percent}
                    data-attribute={`${imageWidth}-${item.max_width}-${dimensions.width
                      }-${pt2px(dimensions.width)}`}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) / pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face,
                    }}
                    dangerouslySetInnerHTML={{ __html: item.text }}></div>
                )
              })}
            <Image
              key={`${bookView}-${forceUpdate}`}
              alt="cover"
              className="w-full h-full object-cover"
              width={700}
              priority={true}
              height={700}
              onLoad={handleImageLoad}
              onError={(e: any) => {
                console.error('Failed to load image:', e);
                e.target.src = '/placeholder.png';
              }}
              src={formatImageUrl(cover_pages[1])}
            />
          </div>

          {/* Main pages */}
          {renderPages.map((page: any, index: number) => {
            const logicalPageNumber = isInIsrael
            ? totalPages - index
            : index - 1;

            const predefinedEntry = pages_predefined_texts.find(
              (x: any) => x.page === logicalPageNumber.toString()
            );

            const isWhitePage = index === 0;
            const dedicationPageNumber =
              isInIsrael ? (imageSelected === 'yes' && dedicationImage ? '1' : '2') : (imageSelected === 'yes' && dedicationImage ? (filteredPages.length-2).toString() : (modified_pages.length-3).toString());

              const dedicationPageIndexInRender = filteredPages.findIndex((p: any) => 
                p === dedicationPage
              );

            const isDedicationPage =
            isInIsrael ? 
            (imageSelected === 'yes' ? (index === (filteredPages.length-2) - dedicationPageIndexInRender) : (logicalPageNumber.toString() === (dedicationPageNumber))) : 
            (index === (filteredPages.length-1) - dedicationPageIndexInRender);
            
            return (
              <div className="relative" key={`dedication-image-${index}-${forceUpdate}`}>
                {isWhitePage ? (
                  <div className="w-full h-full bg-white"></div>
                ) : (
                  <Image
                    key={`${bookView}-${index}-${forceUpdate}`}
                    className={`block relative w-full book-image img-${index}`}
                    alt="book-image"
                    width={700}
                    height={700}
                    onLoad={handleImageLoad}
                    onError={(e: any) => {
                      console.error('Failed to load image:', e);
                      e.target.src = '/placeholder.png';
                    }}
                    priority={true}
                    src={formatImageUrl(page)}
                    style={{ display: 'block' }}
                  />
                )}

                {/* Dedication Text/Image */}
                {isDedicationPage && (() => {
                  const dedicationEntry = pages_dedication_texts.find(
                    (x: any) =>
                      x.page === dedicationPageNumber
                  );
                  if (!dedicationEntry){
                    return null;
                  }

                  const dimensions = JSON.parse(dedicationEntry.dimensions);

                  return dedicationEntry.dedication_texts?.map((item: any, idx: number) => {
                    if (!item.text) {
                      return (
                        <div
                          key={`dedication-text-${idx}-${forceUpdate}`}
                          className="absolute z-[20]"
                          style={{
                            top: `${(item.Y_coord / dimensions.height) * 76}%`,
                            left: `${(item.X_coord / dimensions.height) * 74.5}%`,
                            width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                            height: (imageWidth * item.max_width) / pt2px(dimensions.width),
                          }}
                        >
                          {dedicationImage && (
                            <img
                              src={dedicationImage}
                              alt="Child"
                              className="object-cover rounded-full w-full h-full"
                            />
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={`dedication-child-image-${index}-${forceUpdate}`}
                        className="absolute z-[20]"
                        style={{
                          fontSize: Math.max(
                            10,
                            (imageWidth * item.font_size) / pt2px(dimensions.width)
                          ),
                          textAlign: getTextAlignment(item.text_align),
                          width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                          top: item.Y_coord_percent > 0
                            ? `${item.Y_coord_percent}%`
                            : `${(item.Y_coord / dimensions.height) * 70}%`,
                          left: item.X_coord_percent > 0
                            ? `${item.X_coord_percent}%`
                            : `${(item.X_coord / dimensions.height) * 83}%`,
                          color: item.color,
                          lineHeight: item.line_height,
                          whiteSpace: 'pre-wrap',
                          fontFamily: item.font_face,
                        }}
                      >
                        {item.text === 'أدخل النص'
                          ? inputDedication.replace('{basmti}', name ?? product.replace_name)
                          : item.text?.includes('{basmti}')
                          ? item.text.replace('{basmti}', name ?? product.replace_name)
                          : item.text}
                      </div>
                    );
                  });
                })()}

                {/* Predefined Texts */}
                {!isWhitePage &&  predefinedEntry?.predefined_texts?.map((item: any, idx: number) => {
                    const dimensions = JSON.parse(predefinedEntry.dimensions);

                    return (
                      <div
                        className="texts absolute"
                        key={`dedication-text-without-image-${idx}-${forceUpdate}`}
                        style={{
                          fontSize: (imageWidth * item.font_size) / pt2px(dimensions.width),
                          textAlign: getTextAlignment(item.text_align),
                          width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                          top: item.Y_coord_percent + '%',
                          left: item.X_coord_percent + '%',
                          color: item.color,
                          lineHeight: item.line_height,
                          whiteSpace: 'pre-wrap',
                          fontFamily: item.font_face,
                        }}
                        dangerouslySetInnerHTML={{ __html: item.text }}
                      ></div>
                    );
                  })}
              </div>
            );
          })}
          
          {/* Last Cover (page 0 for RTL) */}
          <div className="relative start-cover">
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == endCoverPage)[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter(
                    (x: any) => x.page == endCoverPage,
                  )[0]['dimensions'],
                )
                return (
                  <div
                    className="texts absolute"
                    key={`end-cover-${idx}-${forceUpdate}`}
                    id={item.X_coord_percent}
                    data-attribute={`${imageWidth}-${item.max_width}-${dimensions.width
                      }-${pt2px(dimensions.width)}`}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) / pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width:
                        (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      whiteSpace: 'pre-wrap',
                      left: item.X_coord_percent * 2 + '%',
                      color: item.color,
                      fontFamily: item.font_face,
                    }}
                    dangerouslySetInnerHTML={{ __html: item.text }}></div>
                )
              })}
            <Image
              key={`${bookView}-end-${forceUpdate}`}
              alt="cover"
              className="w-full h-full object-cover"
              width={700}
              height={700}
              onLoad={handleImageLoad}
              onError={(e: any) => {
                console.error('Failed to load image:', e);
                e.target.src = '/placeholder.png';
              }}
              src={formatImageUrl(cover_pages[0])}
            />
          </div>
        </HTMLFlipBook>
      )
    } else {
      // For LTR books
      return (
        // @ts-ignore: Unreachable code error
        <HTMLFlipBook
        key={`book-${isProductRtlInBook ? 'rtl' : 'ltr'}-${bookView}-${forceUpdate}`}
        width={width}
        height={height}
        size="stretch"
        ref={desktopRef}
        minWidth={minWidth}
        maxWidth={maxWidth}
        minHeight={minHeight}
        startPage={0}
        showCover={true}
        maxHeight={maxHeight}
        maxShadowOpacity={0.5}
        mobileScrollSupport={true}
      >
        {/* First Cover (page 0 for LTR) */}
        <div className="relative">
          {cover_pages_predefined_texts
            ?.find((x: any) => x.page == startCoverPage)
            ?.predefined_texts?.map((item: any, idx: number) => {
              const dimensions = JSON.parse(
                cover_pages_predefined_texts.find((x: any) => x.page == startCoverPage)
                  ?.dimensions || '{}'
              );
              return (
                <div
                  className="texts absolute"
                  key={`back-cover-${idx}-${forceUpdate}`}
                  style={{
                    fontSize:
                      (imageWidth * item.font_size) / pt2px(dimensions.width),
                    textAlign: getTextAlignment(item.text_align),
                    width:
                      (imageWidth * item.max_width) / pt2px(dimensions.width),
                    top: item.Y_coord_percent + '%',
                    lineHeight: item.line_height,
                    whiteSpace: 'pre-wrap',
                    left: item.X_coord_percent * 2 + '%',
                    color: item.color,
                    fontFamily: item.font_face,
                    direction: 'ltr',
                  }}
                  dangerouslySetInnerHTML={{ __html: item.text }}
                ></div>
              );
            })}
          <Image
            key={`${bookView}-back-${forceUpdate}`}
            alt="cover"
            className="w-full h-full object-cover"
            width={700}
            priority={true}
            height={700}
            onLoad={handleImageLoad}
            onError={(e: any) => {
              console.error('Failed to load image:', e);
              e.target.src = '/placeholder.png';
            }}
            src={formatImageUrl(cover_pages[0])}
          />
        </div>

        {/* Main Pages */}
        {[...filteredPages].reverse().map((page: any, index: number) => {
        const isWhitePage = index === 0;
        const isDedicationPage = index === 1;
        const dedicationEntry = isDedicationPage
          ? pages_dedication_texts.find(
              (x: any) =>
                x.page === (imageSelected === 'yes' && dedicationImage ? '1' : '2')
            )
          : null;

        const predefinedEntry = pages_predefined_texts.find(
          (x: any) => x.page == (index + 1).toString()
        );

        return (
          <div
            className="relative"
            key={`dedication-page-${index}-${forceUpdate}`}
          >
            {isWhitePage ? 
            (
              <div className="w-full h-full bg-white"></div>
            ):
              (<Image
                className={`block w-full book-image img-${index}`}
                alt="book-image"
                width={700}
                height={700}
                onLoad={handleImageLoad}
                onError={(e: any) => {
                  console.error('Failed to load image:', e);
                  e.target.src = '/placeholder.png';
                }}
                priority={true}
                src={formatImageUrl(page)}
                style={{ display: 'block' }}
              />
              )}

            {/* Dedication Text/Image */}
            {isDedicationPage && dedicationEntry?.dedication_texts?.map((item: any, idx: number) => {
              const dimensions = JSON.parse(dedicationEntry.dimensions);
              const isImageBlock = !item.text;

              if (isImageBlock) {
                return (
                  <div
                    key={`dedication-child-image-${idx}-${forceUpdate}`}
                    className="texts absolute z-[20]"
                    style={{
                      top: `${(item.Y_coord / dimensions.height) * 76}%`,
                      left: `${(item.X_coord / dimensions.width) * 74.5}%`,
                      width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                      height: (imageWidth * item.max_width) / pt2px(dimensions.width),
                    }}
                  >
                    {dedicationImage && (
                      <img
                        src={dedicationImage}
                        alt="Child"
                        className="object-cover rounded-full w-full h-full"
                      />
                    )}
                  </div>
                );
              }

              return (
                <div
                  className="texts absolute z-[20]"
                  key={`dedication-text-${idx}-${forceUpdate}`}
                  style={{
                    fontSize: Math.max(
                      10,
                      (imageWidth * item.font_size) / pt2px(dimensions.width)
                    ),
                    textAlign: getTextAlignment(item.text_align),
                    width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                    top: item.Y_coord_percent + '%',
                    left: item.X_coord_percent + '%',
                    color: item.color,
                    lineHeight: item.line_height,
                    whiteSpace: 'pre-wrap',
                    fontFamily: item.font_face,
                  }}
                >
                  {item.text === 'أدخل النص'
                    ? inputDedication.replace('{basmti}', name ?? product.replace_name)
                    : item.text?.includes('{basmti}')
                    ? item.text.replace('{basmti}', name ?? product.replace_name)
                    : item.text}
                </div>
              );
            })}

            {/* Predefined Texts */}
            {predefinedEntry?.predefined_texts?.map((item: any, idx: number) => {
              const dimensions = JSON.parse(predefinedEntry.dimensions);
              return (
                <div
                  className="texts absolute"
                  key={`predef-${idx}-${forceUpdate}`}
                  style={{
                    fontSize:
                      (imageWidth * item.font_size) / pt2px(dimensions.width),
                    textAlign: getTextAlignment(item.text_align),
                    width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                    top: item.Y_coord_percent + '%',
                    lineHeight: item.line_height,
                    whiteSpace: 'pre-wrap',
                    color: item.color,
                    left: item.X_coord_percent + '%',
                    fontFamily: item.font_face,
                    direction: 'ltr',
                  }}
                  dangerouslySetInnerHTML={{ __html: item.text }}
                ></div>
              );
            })}
          </div>
        );
      })}

        {/* Last Cover (page 1 for LTR) */}
        <div className="relative start-cover">
          {cover_pages_predefined_texts
            ?.find((x: any) => x.page == endCoverPage)
            ?.predefined_texts?.map((item: any, idx: number) => {
              const dimensions = JSON.parse(
                cover_pages_predefined_texts.find((x: any) => x.page == endCoverPage)
                  ?.dimensions || '{}'
              );
              return (
                <div
                  className="texts absolute"
                  key={`${bookView}-page-${idx}-${forceUpdate}`}
                  style={{
                    fontSize:
                      (imageWidth * item.font_size) / pt2px(dimensions.width),
                    textAlign: getTextAlignment(item.text_align),
                    width:
                      (imageWidth * item.max_width) / pt2px(dimensions.width),
                    top: item.Y_coord_percent + '%',
                    lineHeight: item.line_height,
                    whiteSpace: 'pre-wrap',
                    left: item.X_coord_percent * 2 + '%',
                    color: item.color,
                    fontFamily: item.font_face,
                    direction: 'ltr',
                  }}
                  dangerouslySetInnerHTML={{ __html: item.text }}
                ></div>
              );
            })}
          <Image
            key={`${bookView}-end-cover-${forceUpdate}`}
            alt="cover"
            className="w-full h-full object-cover"
            width={700}
            height={700}
            onLoad={handleImageLoad}
            onError={(e: any) => {
              console.error('Failed to load image:', e);
              e.target.src = '/placeholder.png';
            }}
            src={formatImageUrl(cover_pages[1])}
          />
        </div>
      </HTMLFlipBook>
      )
    }
  }
  return <></>
}